import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import FilterDropdown from '../common/FilterDropdown';
import { useAppState } from '../../context/AppStateContext';

const TARGET_TYPE_OPTIONS = [
    { name: 'School Wide', value: 'all' },
    { name: 'Academic Level', value: 'grades' },
    { name: 'Extra-curricular Activities', value: 'clubs' }
];

const EventNoticeModal = ({ event = null, onClose, onSuccess }) => {
    const { get, post, put } = useApi();
    const { showToast } = useAppState();
    const [grades, setGrades] = useState([]);
    const [clubs, setClubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        target_grades: [],
        target_streams: [],
        target_clubs: [],
        start_datetime: '',
        end_datetime: '',
        location: '',
        has_fee: false,
        cost: 0
    });
    const [academicValue, setAcademicValue] = useState('');
    const [clubValue, setClubValue] = useState('');

    const fetchData = useCallback(async () => {
        try {
            const [gradeData, clubData] = await Promise.all([
                get('/api/cbc/grade-levels/'),
                get('/api/events/clubs/')
            ]);
            setGrades(gradeData);
            setClubs(clubData);
            if (event) {
                setFormData({
                    title: event.title,
                    description: event.description || '',
                    target_type: event.target_type || 'all',
                    target_grades: event.target_grades || [],
                    target_streams: event.target_streams || [],
                    target_clubs: event.target_clubs || [],
                    start_datetime: event.start_datetime ? event.start_datetime.slice(0, 16) : '',
                    end_datetime: event.end_datetime ? event.end_datetime.slice(0, 16) : '',
                    location: event.location || '',
                    has_fee: event.has_fee || false,
                    cost: event.cost || 0
                });
                
                if (event.target_type === 'grades') {
                    if (event.target_streams?.length > 0 && event.target_grades?.length > 0) {
                        setAcademicValue(`stream_${event.target_streams[0]}_grade_${event.target_grades[0]}`);
                    } else if (event.target_grades?.length > 0) {
                        setAcademicValue(`grade_${event.target_grades[0]}`);
                    }
                } else if (event.target_type === 'clubs' && event.target_clubs?.length > 0) {
                    setClubValue(`club_${event.target_clubs[0]}`);
                }
            }
        } catch (error) {
            console.error('Error fetching event modal data:', error);
        } finally {
            setLoading(false);
        }
    }, [get, event]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const [imageFile, setImageFile] = useState(null);


    const handleSubmit = async (e) => {
        e.preventDefault();

        // Use FormData for file upload
        const data = new FormData();
        data.append('title', formData.title);
        data.append('description', formData.description);
        data.append('target_type', formData.target_type);
        data.append('start_datetime', formData.start_datetime);
        data.append('end_datetime', formData.end_datetime);
        data.append('location', formData.location);
        data.append('has_fee', formData.has_fee);
        data.append('cost', formData.cost);

        formData.target_grades.forEach(id => data.append('target_grades', id));
        formData.target_streams.forEach(id => data.append('target_streams', id));
        formData.target_clubs.forEach(id => data.append('target_clubs', id));

        if (imageFile) {
            data.append('image', imageFile);
        }

        try {
            // Note: useApi hooks usually handle Content-Type automatically if body is FormData,
            // or we might need to rely on the underlying fetch/axios behavior.
            // Assuming useApi 'post'/'put' can handle FormData.
            const config = { headers: { 'Content-Type': 'multipart/form-data' } };

            if (event) {
                // If using axios via useApi, we might need to pass headers or let it infer. 
                // Since I can't easily see useApi implementation details for FormData, I'll assume standard fetch/axios behavior
                // which automatically sets boundary for FormData if Content-Type is NOT forced, or we let the client handle it.
                // However, often standard `post(url, data)` works. Let's try standard.
                await put(`/api/events/notices/${event.id}/`, data);
            } else {
                await post('/api/events/notices/', data);
            }
            onSuccess?.();
            showToast(event ? 'Event notice updated' : 'Event deployed successfully', 'success');
            onClose();
        } catch (error) {
            console.error('Error saving event notice:', error);
            showToast(error.response?.data?.detail || 'Could not save event notice. Please check all fields.', 'error');
        }
    };

    const handleMultiSelect = (field, value) => {
        const currentSelection = formData[field];
        if (currentSelection.includes(value)) {
            setFormData({ ...formData, [field]: currentSelection.filter(id => id !== value) });
        } else {
            setFormData({ ...formData, [field]: [...currentSelection, value] });
        }
    };

    const academicOptions = React.useMemo(() => {
        const uniqueValues = new Set();
        const opts = [];
        grades.flatMap(g => [
            { name: g.name, value: `grade_${g.id}` },
            ...(g.streams || []).map(s => ({
                name: `${g.name} - ${s.name}`,
                value: `stream_${s.id}_grade_${g.id}`
            }))
        ]).forEach(opt => {
            if (!uniqueValues.has(opt.value)) {
                uniqueValues.add(opt.value);
                opts.push(opt);
            }
        });
        return opts;
    }, [grades]);

    const clubOptions = React.useMemo(() => {
        const uniqueValues = new Set();
        const opts = [];
        clubs
            .filter(c => c.category !== 'cocurricular')
            .map(c => ({ name: c.name, value: `club_${c.id}` }))
            .forEach(opt => {
                if (!uniqueValues.has(opt.value)) {
                    uniqueValues.add(opt.value);
                    opts.push(opt);
                }
            });
        return opts;
    }, [clubs]);

    if (loading && !event) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}></div>
            <div className="relative bg-white w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 flex flex-col max-h-[90vh]">
                <div className="p-6 border-b border-slate-50 flex-shrink-0 flex items-start justify-between">
                    <div>
                        <h2 className="text-2xl font-black text-[#18216D] italic uppercase tracking-tighter">
                            {event ? 'Modify Notice Protocol' : 'Deploy New Notice'}
                        </h2>
                        <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mt-1">Institutional Communication Hub</p>
                    </div>
                    <button
                        onClick={onClose}
                        type="button"
                        className="text-slate-400 hover:text-slate-600 transition-colors p-2 rounded-lg hover:bg-slate-50"
                    >
                        <i className="fas fa-times text-xl"></i>
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="p-10 space-y-8 overflow-y-auto custom-scrollbar">
                    <div className="grid grid-cols-2 gap-6">
                        <div className="col-span-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Notice Headline</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                required
                                className="w-full bg-white border-2 border-slate-300 rounded-2xl px-6 py-4 text-sm font-bold text-[#18216D] focus:ring-4 focus:ring-indigo-50 transition-all outline-none"
                                placeholder="e.g., Annual Sports Day 2026"
                            />
                        </div>
                        <div className="col-span-2 grid grid-cols-2 gap-6">
                            <div>
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Start Date</label>
                                <input
                                    type="datetime-local"
                                    value={formData.start_datetime}
                                    onChange={(e) => setFormData({ ...formData, start_datetime: e.target.value })}
                                    required
                                    className="w-full bg-white border-2 border-slate-300 rounded-2xl px-6 py-4 text-sm font-black text-[#18216D] focus:ring-4 focus:ring-indigo-50 transition-all outline-none"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">End Date</label>
                                <input
                                    type="datetime-local"
                                    value={formData.end_datetime}
                                    onChange={(e) => setFormData({ ...formData, end_datetime: e.target.value })}
                                    required
                                    className="w-full bg-white border-2 border-slate-300 rounded-2xl px-6 py-4 text-sm font-black text-[#18216D] focus:ring-4 focus:ring-indigo-50 transition-all outline-none"
                                />
                            </div>
                        </div>

                        <div className="col-span-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Location</label>
                            <input
                                type="text"
                                required
                                value={formData.location}
                                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                className="w-full bg-white border-2 border-slate-300 rounded-2xl px-6 py-4 text-sm font-bold text-[#18216D] focus:ring-4 focus:ring-indigo-50 transition-all outline-none"
                                placeholder="e.g. School Hall, Sports Field"
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Event Banner (Optional)</label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => setImageFile(e.target.files[0])}
                                className="w-full bg-white border-2 border-slate-300 rounded-2xl px-6 py-4 text-sm font-bold text-[#18216D] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-[10px] file:font-black file:uppercase file:tracking-widest file:bg-[#18216D] file:text-white hover:file:bg-indigo-900 outline-none"
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Detailed Description</label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="w-full bg-white border-2 border-slate-300 rounded-2xl px-6 py-4 text-sm font-bold text-[#18216D] focus:ring-4 focus:ring-indigo-50 transition-all h-24 outline-none"
                                placeholder="Outline event specifics, requirements, and logistics..."
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Targeting Paradigm</label>
                            <FilterDropdown
                                value={formData.target_type}
                                onChange={(val) => {
                                    setAcademicValue('');
                                    setClubValue('');
                                    setFormData(prev => ({ ...prev, target_type: val, target_grades: [], target_streams: [], target_clubs: [] }));
                                }}
                                options={TARGET_TYPE_OPTIONS}
                                placeholder="Select Targeting Paradigm"
                                className="w-full"
                            />
                        </div>

                        {formData.target_type === 'grades' && (
                            <div className="col-span-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Select Academic Level</label>
                                <FilterDropdown
                                    value={academicValue}
                                    onChange={(val) => {
                                        setAcademicValue(val);
                                        let newGrades = [];
                                        let newStreams = [];
                                        if (val.startsWith('grade_')) {
                                            newGrades.push(parseInt(val.split('_')[1]));
                                        } else if (val.startsWith('stream_')) {
                                            const parts = val.split('_');
                                            newStreams.push(parseInt(parts[1]));
                                            newGrades.push(parseInt(parts[3]));
                                        }
                                        setFormData(prev => ({ ...prev, target_grades: newGrades, target_streams: newStreams }));
                                    }}
                                    options={academicOptions}
                                    placeholder="Select Grade or Stream"
                                    className="w-full"
                                />
                            </div>
                        )}

                        {formData.target_type === 'clubs' && (
                            <div className="col-span-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Select Extra-curricular Activity</label>
                                <FilterDropdown
                                    value={clubValue}
                                    onChange={(val) => {
                                        setClubValue(val);
                                        let newClubs = [];
                                        if (val) {
                                            newClubs.push(parseInt(val.split('_')[1]));
                                        }
                                        setFormData(prev => ({ ...prev, target_clubs: newClubs }));
                                    }}
                                    options={clubOptions}
                                    placeholder="Select Extra-curricular Activity"
                                    className="w-full"
                                />
                            </div>
                        )}

                        <div className="col-span-2 flex items-center gap-6 p-6 bg-amber-50 rounded-[2rem] border border-amber-100/50 shadow-sm">
                            <div className="flex items-center gap-4">
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={formData.has_fee}
                                        onChange={(e) => setFormData({ ...formData, has_fee: e.target.checked })}
                                    />
                                    <div className="w-11 h-6 bg-amber-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#18216D]"></div>
                                </label>
                                <span className="text-[10px] font-black text-amber-700 uppercase tracking-widest">Mandatory Fee Protocol</span>
                            </div>
                            {formData.has_fee && (
                                <div className="flex-1 flex items-center gap-3">
                                    <span className="text-sm font-black text-[#18216D]">KES</span>
                                    <input
                                        type="number"
                                        value={formData.cost}
                                        onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) })}
                                        className="flex-1 bg-white border-none rounded-xl px-4 py-2 text-sm font-black text-[#18216D] focus:ring-2 focus:ring-[#18216D]/10"
                                        placeholder="Amount"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="pt-6 flex gap-4 flex-shrink-0">
                        <button type="button" onClick={onClose} className="flex-1 py-4 bg-slate-50 text-slate-400 rounded-2xl text-[10px] font-black uppercase tracking-widest">Revoke deployment</button>
                        <button type="submit" className="flex-1 py-4 bg-[#18216D] text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-indigo-900/10">Synchronize Calendar</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EventNoticeModal;
